import { useMemo } from "react";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG, PixiSettings } from "../../../../pixiSettings";

// Override shared config — sound button is slightly smaller on mobile portrait
const SOUND_BUTTON_CONFIG = {
  ...USER_INTERFACE_BUTTON_CONFIG,
  DEVICE_CONFIGS: {
    ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS,
    [PixiSettings.DEVICE.MOBILE]: {
      ...USER_INTERFACE_BUTTON_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE],
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.2,
      },
    },
  },
};

/**
 * useSoundButtonLayout — the sound button's size and centre (anchor 0.5), in
 * top-toolbar coordinates. ONE source (2026-10-06): ButtonSound draws itself
 * from it, and the Clock places itself beside it from the same numbers, so
 * the game name never has to be positioned by hand for a new game.
 *
 * @param {{ width: number }} toolbar the secondary top toolbar
 * @returns {{ buttonConfig: { width: number, height: number }, buttonPosition: { x: number, y: number } }}
 */
export const useSoundButtonLayout = (toolbar) => {
  const { getResponsiveConfig, device, orientation, isMobileSmall } = useDimensions();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(SOUND_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const buttonPosition = useMemo(() => {
    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width - buttonConfig.width * 0.925,
          y: buttonConfig.height * 1.3,
        };
      } else {
        return {
          x: buttonConfig.width * 0.6,
          y: buttonConfig.height * 0.6,
        };
      }
    }
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width - buttonConfig.width * 0.85,
          y: buttonConfig.height * 1.6,
        };
      } else {
        return {
          x: buttonConfig.width * 0.6,
          y: buttonConfig.height * 0.7,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width - buttonConfig.width * 0.8,
          y: buttonConfig.width * 0.8,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.8,
          y: buttonConfig.width * 0.8,
        };
      }
    } else {
      // desktop
      return {
        x: toolbar.width - buttonConfig.width,
        y: buttonConfig.width * 0.8,
      };
    }
  }, [device, orientation, toolbar.width, buttonConfig.width, buttonConfig.height, isMobileSmall]);

  return { buttonConfig, buttonPosition };
};

export default useSoundButtonLayout;
